import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useRegion, Region } from '../../contexts/RegionContext';

const SelectorContainer = styled.div`
  position: relative;
`;

const SelectorButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 180px;

  &:hover {
    background: white;
    border-color: #6366f1;
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.15);
  }
`;

const RegionIcon = styled.div<{ $color?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.$color || '#6366f1'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const RegionInfo = styled.div`
  flex: 1;
  text-align: left;
`;

const RegionName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
`;

const RegionLabel = styled.div`
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
`;

const DropdownArrow = styled.span<{ $open: boolean }>`
  display: flex;
  transition: transform 0.2s ease;
  transform: ${props => props.$open ? 'rotate(180deg)' : 'rotate(0)'};
  color: #64748b;
`;

const Dropdown = styled.div<{ $open: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  min-width: 260px;
  opacity: ${props => props.$open ? 1 : 0};
  visibility: ${props => props.$open ? 'visible' : 'hidden'};
  transform: ${props => props.$open ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
  z-index: 1000;
  overflow: hidden;
`;

const DropdownHeader = styled.div`
  padding: 14px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
`;

const DropdownTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RegionList = styled.div`
  max-height: 320px;
  overflow-y: auto;
  padding: 8px;
`;

const RegionItem = styled.button<{ $active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: none;
  border-radius: 8px;
  background: ${props => props.$active ? 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)' : 'transparent'};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:hover {
    background: ${props => props.$active ? 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)' : '#f8fafc'};
  }
`;

const RegionItemIcon = styled.div<{ $color?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.$color || '#6366f1'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
`;

const RegionItemInfo = styled.div`
  flex: 1;
`;

const RegionItemName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`;

const RegionItemCompliance = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
`;

const ActiveIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
`;

export const RegionSelector: React.FC = () => {
  const { selectedRegion, setSelectedRegion, regions } = useRegion();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRegionSelect = (region: Region) => {
    setSelectedRegion(region);
    setIsOpen(false);
  };

  return (
    <SelectorContainer ref={containerRef}>
      <SelectorButton onClick={() => setIsOpen(!isOpen)}>
        <RegionIcon $color={selectedRegion.color}>
          {selectedRegion.flag}
        </RegionIcon>
        <RegionInfo>
          <RegionName>{selectedRegion.name}</RegionName>
          <RegionLabel>{selectedRegion.compliance} Compliance</RegionLabel>
        </RegionInfo>
        <DropdownArrow $open={isOpen}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </DropdownArrow>
      </SelectorButton>

      <Dropdown $open={isOpen}>
        <DropdownHeader>
          <DropdownTitle>🌐 Select Data Region</DropdownTitle>
        </DropdownHeader>

        <RegionList>
          {regions.map((region) => (
            <RegionItem
              key={region.code}
              $active={selectedRegion.code === region.code}
              onClick={() => handleRegionSelect(region)}
            >
              <RegionItemIcon $color={region.color}>
                {region.flag}
              </RegionItemIcon>
              <RegionItemInfo>
                <RegionItemName>{region.name}</RegionItemName>
                <RegionItemCompliance>
                  {region.code === 'ALL' ? 'View all regions' : `${region.compliance} Compliance`}
                </RegionItemCompliance>
              </RegionItemInfo>
              {selectedRegion.code === region.code && <ActiveIndicator />}
            </RegionItem>
          ))}
        </RegionList>
      </Dropdown>
    </SelectorContainer>
  );
};

export default RegionSelector;
